import LocalStorageService from "./LocalStorageService";

export const ALPHABET = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
  "AA",
  "AB",
  "AC",
  "AD",
  "AE",
  "AF",
  "AG",
  "AH",
  "AI",
  "AJ",
  "AK",
  "AL",
  "AM",
  "AO",
  "AP",
  "AQ",
  "AR",
  "AS",
  "AT",
  "AU",
  "AV",
  "AW",
  "AX",
  "AY",
  "AZ",
];

// Label of the idx-th observation of a pass; numbered once the alphabet is exhausted
export function getLabelForIndex(idx: number): string {
  return idx < ALPHABET.length ? ALPHABET[idx] : String(idx + 1);
}

const COLORS = [
  "#e88fe8",
  "#1E90FF",
  "#8c00ff",
  "#27a3bc",
  "#FFA07A",
  "#A9A9A9",
  "#bd9d6b",
  "#FF8C00",
  "#90EE90",
  "#C71585",
  "#191970",
  "#A0522D",
  "#B0C4DE",
  "#FFD700",
  "#FF6347",
  "#008080",
  "#9966CC",
  "#E6E6FA",
  "#F8F8FF",
];

// 360 hues x 2 lightness levels
const GENERATED_COLORS_COUNT = 720;

export interface LetterBadge {
  color?: string;
  label: string;
}

export default class LetterBadgeManager {
  private letterPerHash: Map<string, string> = new Map<string, string>();
  private colorPerHash: Map<string, string> = new Map<string, string>();
  private idxLetter = 0;
  private idxColor = 0;

  getLetterBadge(type: string, hash: string): LetterBadge {
    if (!type || !hash) return;

    const letter = this.letterPerHash.get(hash);

    if (!letter) {
      this.letterPerHash.set(hash, this.nextLetter());
    }

    return {
      color: LocalStorageService.getObservationType(type).color,
      label: this.letterPerHash.get(hash),
    };
  }

  getLetterBadgeRandomColor(hash: string): LetterBadge {
    if (!hash) return;

    const letter = this.letterPerHash.get(hash);

    if (!letter) {
      this.letterPerHash.set(hash, this.nextLetter());
    }

    const color = this.colorPerHash.get(hash);

    if (!color) {
      this.colorPerHash.set(hash, this.nextColor());
    }

    return {
      color: this.colorPerHash.get(hash),
      label: this.letterPerHash.get(hash),
    };
  }

  unsetTagForObs(hash: any) {
    this.letterPerHash.set(hash, null);
    this.colorPerHash.set(hash, null);
  }

  private nextColor(): string {
    for (let i = 0; i < COLORS.length; i++) {
      const color = COLORS[this.idxColor++ % COLORS.length];
      if (!LetterBadgeManager.containsValue(this.colorPerHash, color)) {
        return color;
      }
    }

    // Palette exhausted: generate distinct hues (golden angle) instead of capping the number of observations
    for (let n = 0; n < GENERATED_COLORS_COUNT; n++) {
      const color = LetterBadgeManager.generatedColor(n);
      if (!LetterBadgeManager.containsValue(this.colorPerHash, color)) {
        return color;
      }
    }

    // Every generated color is taken too: reuse one rather than blocking the observation
    return LetterBadgeManager.generatedColor(this.colorPerHash.size);
  }

  private static generatedColor(n: number): string {
    return `hsl(${Math.round((n * 137.508) % 360)}, 70%, ${n % 2 === 0 ? 55 : 70}%)`;
  }

  private nextLetter(): string {
    for (let i = 0; i < ALPHABET.length; i++) {
      const letter = ALPHABET[this.idxLetter++ % ALPHABET.length];
      if (!LetterBadgeManager.containsValue(this.letterPerHash, letter)) {
        return letter;
      }
    }

    // Alphabet exhausted: fall back to numbered labels
    for (let n = ALPHABET.length + 1; ; n++) {
      const letter = String(n);
      if (!LetterBadgeManager.containsValue(this.letterPerHash, letter)) {
        return letter;
      }
    }
  }

  reset() {
    this.letterPerHash.clear();
    this.idxLetter = 0;
    this.idxColor = 0;
  }

  static containsValue(map: Map<string, string>, val: string): boolean {
    for (const [k, v] of map) {
      if (v === val) {
        return true;
      }
    }

    return false;
  }
}
